<template>
  <v-dialog
    v-model="dialog"
    max-width="700"
    persistent
  >
    <v-card>
      <v-card-title>
        <v-icon left>mdi-file-document-check</v-icon>
        ກວດສອບຫຼັກຖານການຈ່າຍ
      </v-card-title>

      <v-card-text>
        <!-- Record info -->
        <div class="mb-4 pa-3 grey darken-3 rounded">
          <div class="text-caption grey--text">ລາຍການທະນາຄານ</div>
          <div class="d-flex justify-space-between mt-1">
            <span>Ref: <strong>{{ record.transaction_ref }}</strong></span>
            <span>ຈຳນວນ: <strong>{{ formatCurrency(record.amount) }} LAK</strong></span>
            <span>ວັນທີ: <strong>{{ record.statement_date }}</strong></span>
          </div>
        </div>

        <!-- Step 1: Upload -->
        <div v-if="!ocrResult">
          <div class="text-subtitle-2 mb-2">ອັບໂຫຼດຮູບຫຼັກຖານ (ภาพ screenshot การชำระ)</div>
          <v-file-input
            v-model="evidenceFile"
            accept="image/jpeg,image/png,image/webp"
            label="ເລືອກຮູບພາບ"
            outlined
            dense
            prepend-icon=""
            prepend-inner-icon="mdi-camera"
            hint="JPG, PNG, WebP (max 5MB)"
            persistent-hint
          />
          <div v-if="evidenceFile" class="text-center my-3">
            <v-img
              :src="previewUrl"
              max-height="300"
              contain
              class="rounded"
            />
          </div>
          <v-btn
            color="primary"
            depressed
            block
            :loading="uploading"
            :disabled="!evidenceFile"
            @click="uploadEvidence"
          >
            <v-icon left>mdi-eye-check</v-icon>
            ກວດສອບ
          </v-btn>
        </div>

        <!-- Step 2: OCR Results -->
        <div v-if="ocrResult">
          <div class="text-subtitle-2 mb-3">ຜົນການກວດສອບ</div>

          <v-simple-table dense>
            <tbody>
              <tr v-for="(check, field) in ocrResult.matches" :key="field">
                <td class="text-body-2" style="width: 120px">
                  {{ fieldLabel(field) }}
                </td>
                <td>
                  <v-icon
                    small
                    :color="check.match ? 'green' : (check.extracted !== null && check.extracted !== undefined ? 'red' : 'grey')"
                  >
                    {{ check.match ? 'mdi-check-circle' : (check.extracted !== null && check.extracted !== undefined ? 'mdi-close-circle' : 'mdi-minus-circle') }}
                  </v-icon>
                </td>
                <td class="text-body-2">
                  <span v-if="check.extracted !== null && check.extracted !== undefined">
                    {{ check.extracted }}
                  </span>
                  <span v-else class="grey--text">ບໍ່ພົບ</span>
                </td>
                <td class="text-body-2 grey--text">
                  <span v-if="check.expected">
                    (ຄາດຫວັງ: {{ check.expected }})
                  </span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <!-- Mismatch warning -->
          <v-alert
            v-if="ocrResult.mismatches && ocrResult.mismatches.length > 0"
            type="warning"
            text
            dense
            class="mt-3"
          >
            ⚠ ບໍ່ກົງກັນ: {{ ocrResult.mismatches.join(', ') }}
          </v-alert>

          <!-- All match -->
          <v-alert
            v-else-if="ocrResult.overall_status === 'matched'"
            type="success"
            text
            dense
            class="mt-3"
          >
            ✓ ຂໍ້ມູນທັງໝົດກົງກັນ
          </v-alert>

          <!-- Notes for mismatch -->
          <v-textarea
            v-if="ocrResult.mismatches && ocrResult.mismatches.length > 0"
            v-model="confirmNotes"
            label="ເຫດຜົນ (ຖ້າຕ້ອງການຢືນຢັນທັງທີ່ບໍ່ກົງ)"
            outlined
            rows="2"
            dense
            class="mt-3"
          />

          <!-- Actions -->
          <div class="d-flex mt-4">
            <v-btn
              text
              @click="resetOcr"
            >
              <v-icon left small>mdi-refresh</v-icon>
              ອັບໂຫຼດໃໝ່
            </v-btn>
            <v-spacer />
            <v-btn
              text
              class="mr-2"
              @click="rejectEvidence"
              :loading="confirming"
            >
              ປະຕິເສດ
            </v-btn>
            <v-btn
              color="success"
              depressed
              @click="confirmMatch"
              :loading="confirming"
              :disabled="ocrResult.mismatches && ocrResult.mismatches.length > 0 && !confirmNotes"
            >
              <v-icon left>mdi-check</v-icon>
              ຢືນຢັນ
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-card-actions v-if="!ocrResult">
        <v-spacer />
        <v-btn text @click="close">ປິດ</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'EvidenceDialog',
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      evidenceFile: null,
      uploading: false,
      confirming: false,
      ocrResult: null,
      confirmNotes: '',
    };
  },
  computed: {
    dialog: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },
    previewUrl() {
      return this.evidenceFile ? URL.createObjectURL(this.evidenceFile) : '';
    },
  },
  watch: {
    value(v) {
      if (!v) this.reset();
    },
  },
  methods: {
    async uploadEvidence() {
      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append('file', this.evidenceFile);
        formData.append('statement_record_id', this.record.id);
        const res = await this.$axios.post('ldb-reconciliation/upload-evidence', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        this.ocrResult = res.data.data;
      } catch (e) {
        const msg = e.response?.data?.message || 'ກວດສອບລົ້ມເຫຼວ';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.uploading = false;
      }
    },
    async confirmMatch() {
      this.confirming = true;
      try {
        await this.$axios.post('ldb-reconciliation/confirm-evidence', {
          statement_record_id: this.record.id,
          confirmed: true,
          notes: this.confirmNotes || null,
        });
        this.$store.commit('Toast_State', { value: true, color: 'success', msg: 'ຢືນຢັນສຳເລັດ' });
        this.$emit('confirmed', this.record);
        this.close();
      } catch (e) {
        const msg = e.response?.data?.message || 'ຢືນຢັນລົ້ມເຫຼວ';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.confirming = false;
      }
    },
    async rejectEvidence() {
      this.confirming = true;
      try {
        await this.$axios.post('ldb-reconciliation/confirm-evidence', {
          statement_record_id: this.record.id,
          confirmed: false,
        });
        this.$store.commit('Toast_State', { value: true, color: 'info', msg: 'ຫຼັກຖານຖືກປະຕິເສດ' });
        this.close();
      } catch (e) {
        const msg = e.response?.data?.message || 'ລົ້ມເຫຼວ';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.confirming = false;
      }
    },
    resetOcr() {
      this.ocrResult = null;
      this.evidenceFile = null;
      this.confirmNotes = '';
    },
    reset() {
      this.resetOcr();
      this.uploading = false;
      this.confirming = false;
    },
    close() {
      this.dialog = false;
    },
    fieldLabel(field) {
      const labels = {
        amount: 'ຈຳນວນເງິນ',
        reference: 'ເລກອ້າງອິງ',
        date: 'ວັນທີ',
        customer: 'ລູກຄ້າ',
        bill_id: 'ເລກບິນ',
      };
      return labels[field] || field;
    },
    formatCurrency(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-US');
    },
  },
};
</script>
